// Wall-clock arithmetic in a schedule's timezone, done with Intl so it holds
// for any zone the picker offers without a table of transition dates (M202).
//
// The backend (data_functions/triggers.py, compute_next_run_at) builds each
// occurrence as a wall time in the schedule's timezone. A wall time that does
// not exist on a day — the clocks go forward across it — fires at the first
// valid instant after it (01:30 Europe/London on the last Sunday of March is
// 02:30 BST); a wall time that occurs twice fires at its first occurrence.
// This module tells the picker about the former so it can say so.

const DAY_MS = 24 * 60 * 60 * 1000;

const formatters = {};
const formatter = (timeZone) => {
  if (!formatters[timeZone]) {
    formatters[timeZone] = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return formatters[timeZone];
};

// The wall clock in `timeZone` at the instant `ms`.
export const wallClockAt = (timeZone, ms) => {
  const parts = formatter(timeZone).formatToParts(new Date(ms));
  const read = (type) => Number(parts.find((part) => part.type === type)?.value);
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour') % 24,
    minute: read('minute'),
  };
};

// The zone's offset from UTC at `ms`, in milliseconds.
const offsetAt = (timeZone, ms) => {
  const wall = wallClockAt(timeZone, ms);
  return Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute) - ms;
};

// Whether `hour:minute` exists in `timeZone` on the local date `{year, month,
// day}`. When it does not, `actualHour:actualMinute` is the wall time the run
// happens at instead: the wall time read with the offset in force before the
// change, which is what the backend's candidate resolves to.
export const wallTimeOn = (timeZone, { year, month, day }, hour, minute) => {
  const dayUtc = Date.UTC(year, month - 1, day);
  // Transitions happen in the small hours, never twice within two days, so the
  // offsets a day either side are the ones in force before and after.
  const before = offsetAt(timeZone, dayUtc - DAY_MS);
  const after = offsetAt(timeZone, dayUtc + DAY_MS);
  const wallUtc = Date.UTC(year, month - 1, day, hour, minute);
  const matches = (wall) => (
    wall.year === year && wall.month === month && wall.day === day
    && wall.hour === hour && wall.minute === minute
  );
  if ([before, after].some((offset) => matches(wallClockAt(timeZone, wallUtc - offset)))) {
    return { exists: true };
  }
  const actual = wallClockAt(timeZone, wallUtc - before);
  return { exists: false, actualHour: actual.hour, actualMinute: actual.minute };
};

const addDays = ({ year, month, day }, days) => {
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
};

// Monday = 0 … Sunday = 6, the schedule's own convention.
const weekdayOf = ({ year, month, day }) => (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;

// The first of the schedule's occurrences within `days` of `from` whose wall
// time does not exist in its timezone, with the wall time the run happens at
// instead; null when every occurrence exists (or the zone is unknown).
export const findClockGap = (config, from = new Date(), days = 366) => {
  const timeZone = config?.timezone || 'Europe/London';
  const hour = Number.isInteger(config?.hour) ? config.hour : 9;
  const minute = Number.isInteger(config?.minute) ? config.minute : 0;
  let start;
  try {
    start = wallClockAt(timeZone, from.getTime());
  } catch (err) {
    return null;
  }
  for (let offset = 0; offset < days; offset += 1) {
    const candidate = addDays(start, offset);
    if (config?.frequency === 'weekly' && weekdayOf(candidate) !== (config.day_of_week ?? 0)) continue;
    if (config?.frequency === 'monthly' && candidate.day !== (config.day_of_month ?? 1)) continue;
    const result = wallTimeOn(timeZone, candidate, hour, minute);
    if (!result.exists) {
      return {
        ...candidate,
        hour,
        minute,
        timeZone,
        actualHour: result.actualHour,
        actualMinute: result.actualMinute,
      };
    }
  }
  return null;
};

const twoDigits = (value) => String(value).padStart(2, '0');

export const describeClockGap = (gap) => {
  if (!gap) return '';
  const date = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  }).format(new Date(Date.UTC(gap.year, gap.month - 1, gap.day)));
  return `${twoDigits(gap.hour)}:${twoDigits(gap.minute)} does not exist in ${gap.timeZone} on ${date} — the clocks go forward. That day's run will happen at ${twoDigits(gap.actualHour)}:${twoDigits(gap.actualMinute)}.`;
};
